// The one word the merge pages show for a run.
//
// Two things decide it: what the server says about the run, and where that answer came from. A run
// the server has forgotten (or one only this browser still remembers) is over whatever its last
// status said, so where the answer came from is read first.

/**
 * @param {Object|null} serverStatus - the run as the status endpoint reports it:
 *   `{ status, total, completed, failed }`. Missing means nothing has come back yet.
 * @param {string} [source] - where that answer came from:
 *   'live'     — the run this browser is following right now
 *   'snapshot' — an earlier run, from what this browser saved of it (read like any other answer)
 *   'expired'  — the run is gone from the server and from this browser
 *   'not_found'— the server has no record of it
 * @returns {string} 'queued' | 'processing' | 'completed' | 'completed_with_errors' | 'expired' |
 *   'not_found'
 */
export function deriveMergeRunStatus(serverStatus, source = 'live') {
  if (source === 'expired') return 'expired';
  if (source === 'not_found') return 'not_found';

  const status = serverStatus?.status;

  if (status === 'not_found') return 'not_found';
  if (status === 'completed') {
    return Number(serverStatus?.failed) > 0 ? 'completed_with_errors' : 'completed';
  }
  if (status === 'processing') return 'processing';

  return 'queued';
}

// What a merge run's rows add up to. This is the one place that turns the server's per-group
// results into the numbers a page shows, so every view of a run — the floating widget, the batch
// page, an inspected run from a snapshot — counts a group as merged or failed the same way.
//
// The server's own `completed` counter is not one of the inputs here on purpose: it counts every
// group that has settled, merged or not, so treating it as "how many merged" would double as a
// success count for groups that actually failed. Whether a group merged lives only on that row's
// own `success` flag.
//
/**
 * @param {Array<{success?: boolean, retryable?: boolean}>|null} results - the batch status
 *   `results` array, or a snapshot's saved copy of it. Missing, and any row in it being missing
 *   or half-written, are both tolerated.
 * @returns {{total: number, succeeded: number, failed: number, retryable: number}}
 */
export function countMergeResults(results) {
  const rows = Array.isArray(results) ? results : [];

  return rows.reduce(
    (totals, row) => {
      const succeeded = Boolean(row?.success);
      return {
        total: totals.total + 1,
        succeeded: totals.succeeded + (succeeded ? 1 : 0),
        failed: totals.failed + (succeeded ? 0 : 1),
        retryable: totals.retryable + (!succeeded && row?.retryable ? 1 : 0),
      };
    },
    { total: 0, succeeded: 0, failed: 0, retryable: 0 }
  );
}
